import { Department, Memo, User } from "@/types"

export const getDepartmentCode = (department: string | Department | null | undefined): any => {
  if (department === undefined || department === null) {
    return "";
  }

  if (typeof department === 'string') {
    return department;
  }

  return department.departmentCode;
}

export const getDepartmentIdByCode = (
  departmentCode: string,
  departments: Department[]
): string | undefined => {
  const foundDepartment = departments.find(
    (department) => department.departmentCode === departmentCode
  );

  return foundDepartment ? foundDepartment.id : undefined;
};

export const getDepartmentById = (
  departmentId: string,
  departments: Department[]
): Department | undefined => {
  const foundDepartment = departments.find(
    (department) => department.id == departmentId
  );

  return foundDepartment ? foundDepartment : undefined;
};

export const getDepartmentByCode = (
  departmentCode: string,
  departments: Department[]
): Department | undefined => {
  const foundDepartment = departments.find(
    (department) => department.departmentCode == departmentCode
  );

  return foundDepartment ? foundDepartment : undefined;
};

export const deleteNestedObjFromMainObj = <
  T extends { id: string;[key: string]: any },
  K extends keyof T
>(
  mainObj: T[],
  level1Id: string,
  nestedObjectKey: K,
  nestedIdToDelete: string
): T[] => {
  return mainObj.map(item => {
    if (item.id != level1Id) return item; // only update the matching parent

    const nested = item[nestedObjectKey];
    if (!Array.isArray(nested)) return item; // safety check

    const updatedNested = nested.filter(
      (n: { id: string }) => n.id != nestedIdToDelete
    );

    return { ...item, [nestedObjectKey]: updatedNested };
  });
};

export const getDepartmentIdByUser = (user: User) => {
  if (!user || !user.department) return "";
  return typeof user.department !== 'string' ? user.department.id : user.department;
}

export const deleteFromMainObj = <
  T extends { id: string;[key: string]: any },
>(
  mainObj: T[],
  id: string
): T[] => {
  return mainObj.filter(item => item.id != id);
};

export const updateFromMainObj = <
  T extends { id: number | string;[key: string]: any },
  K extends keyof T,
  U extends { id: number | string }
>(
  mainObj: T[],
  level1Id: number | string,
  nestedObjectKey: K,
  updatedNestedObj: any
): T[] => {
  return mainObj.map(item => {
    if (item.id !== level1Id) return item; // only update the matching parent

    const nested = item[nestedObjectKey];
    if (!Array.isArray(nested)) return item; // safety check

    const updatedNested = nested.map((n: U) =>
      n.id === updatedNestedObj.id ? { ...n, ...updatedNestedObj } : n
    );

    return { ...item, [nestedObjectKey]: updatedNested };
  });
};

export const updateMainObj = (mainObj: any, targetId: number | string, updatedValues: object) => {
  return mainObj.map((item: any) => {
    if (item.id != targetId) return item;

    return { ...item, ...updatedValues };
  });
}

export const getMatchedObjects = <T extends { id: number | string }>(
  ids: (number | string)[],
  objects: T[]) => {

  const result: T[] = [];

  ids.forEach(id => {
    const found = objects.find(obj => obj.id == id);
    if (found) result.push(found);
  });

  return result;
}

export const formatDate = (date: string) => {
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Australia/Sydney'
    }).format(new Date(date));
  } catch (e) {
    return "";
  }
}

/**
 * Formats a date for the native <input type="date"> field (YYYY-MM-DD)
 * using the Australia/Sydney timezone to prevent one-day shifts.
 */
export const toSydneyYYYYMMDD = (date: string | Date | null | undefined) => {
  if (!date) return "";
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Australia/Sydney'
    }).format(d);
  } catch (e) {
    return "";
  }
}

/**
 * Formats a date to dd/mm/yyyy using the Australia/Sydney timezone.
 */
export const toSydneyDate = (date: string | Date | null | undefined) => {
  if (!date) return "";
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Australia/Sydney'
    }).format(d);
  } catch (e) {
    return "";
  }
}

export const getUser = (users: any[], id: any) => {
  const user = users.find(user => user.id == id);

  if (typeof user === 'undefined') return "";

  return user;
}

export const isArrayOfStrings = (value: any): value is string[] => {
  return (
    Array.isArray(value) &&
    value.every(item => typeof item === "string")
  );
}

export const formatArchiveDate = (dateString: string) => {
  if (!dateString) return "Unknown";
  return toSydneyDate(dateString);
};

export const generateNextMemoNumber = (memos: Memo[]) => {
  if (memos.length === 0) return `MEMO-${new Date().getFullYear()}-001`;

  // Find the highest number used in the current year specifically
  const currentYear = new Date().getFullYear();
  let highestNumberInCurrentYear = 0;

  memos.forEach(memo => {
    const match = memo.number.match(/MEMO-(\d{4})-(\d{3})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const num = parseInt(match[2], 10);

      // Only consider memos from the current year
      if (year === currentYear && num > highestNumberInCurrentYear) {
        highestNumberInCurrentYear = num;
      }
    }
  });

  const nextNumber = highestNumberInCurrentYear + 1;
  return `MEMO-${currentYear}-${nextNumber.toString().padStart(3, '0')}`;
};

export const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Sydney'
  }).format(date);
};

export const formatSydneyDateLong = (date: string | Date | null | undefined) => {
  if (!date) return "";
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Australia/Sydney'
    }).format(d);
  } catch (e) {
    return "";
  }
}

export const formatSydneyDateShort = (date: string | Date | null | undefined) => {
  if (!date) return "";
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Australia/Sydney'
    }).format(d);
  } catch (e) {
    return "";
  }
}

export const formatSydneyTime = (timestamp: string | Date | null | undefined) => {
  if (!timestamp) return "";
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return new Intl.DateTimeFormat('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Sydney'
    }).format(date);
  } catch (e) {
    return "";
  }
};

export const getUserName = (userIdOrObj: any, users: any[], currentUser?: any) => {
  // If userId is populated (object), use its name
  if (userIdOrObj && typeof userIdOrObj === "object" && userIdOrObj.name) {
    return userIdOrObj.name;
  }
  // Otherwise, try to find in users array
  const user = users.find(u => u.id === userIdOrObj);
  if (user) return user.name;
  // Fallback: if the userId matches the current user
  if (currentUser && currentUser.id === userIdOrObj) return currentUser.name;
  // Show the ID so it's never completely unidentifiable
  if (userIdOrObj) return `User #${userIdOrObj}`;
  return "Unknown";
};

export const calculateTimeWithCompany = (startDate: string) => {
  if (!startDate) return "N/A";
  const start = new Date(startDate);
  const today = new Date();

  let years = today.getFullYear() - start.getFullYear();
  let months = today.getMonth() - start.getMonth();

  if (months < 0 || (months === 0 && today.getDate() < start.getDate())) {
    years--;
    months += 12;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(", ") : "Less than a month";
};